import { Head, Link, router } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { FinancialYearSelect } from '@/components/FinancialYearSelect'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  UploadIcon,
  ArrowLeftIcon,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

interface ImportSource {
  value: string
  label: string
  kinds: string[]
}

interface Props {
  import_sources: ImportSource[]
}

function humanize(value: string) {
  return value.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

export default function Advanced({ import_sources }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [source, setSource] = useState(import_sources[0]?.value || '')

  const currentSource = useMemo(
    () => import_sources.find((s) => s.value === source) || import_sources[0],
    [import_sources, source]
  )

  const [kind, setKind] = useState(currentSource?.kinds[0] || '')

  function handleSourceChange(nextSource: string) {
    setSource(nextSource)
    const next = import_sources.find((s) => s.value === nextSource)
    setKind(next?.kinds[0] || '')
  }

  function handleImport(e: React.FormEvent) {
    e.preventDefault()
    const file = fileRef.current?.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('source', source)
    formData.append('kind', kind)
    formData.append('file', file)

    router.post('/transactions/import', formData, {
      forceFormData: true,
    })
  }

  const showKindSelect = (currentSource?.kinds.length || 0) > 1

  return (
    <>
      <Head title="Import Transactions" />

      <PageHeader
        title="Import Transactions"
        description="Import transactions from CSV files and manage data"
      >
        <FinancialYearSelect />
        <Link href="/transactions">
          <Button variant="outline" size="sm" className="gap-1.5">
            <ArrowLeftIcon className="h-3.5 w-3.5" />
            Back
          </Button>
        </Link>
      </PageHeader>

      <PageContent className="space-y-5">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/8 p-2">
                <UploadIcon className="h-4 w-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-[15px]">Import from CSV</CardTitle>
                <CardDescription className="text-[13px]">
                  Upload transaction data from your bank's CSV export
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleImport} className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="w-full md:w-[180px]">
                <Label className="text-[13px] font-medium">Source</Label>
                <Select value={source} onValueChange={handleSourceChange}>
                  <SelectTrigger className="mt-1.5 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {import_sources.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {showKindSelect && (
                <div className="w-full md:w-[180px]">
                  <Label className="text-[13px] font-medium">Import Type</Label>
                  <Select value={kind} onValueChange={setKind}>
                    <SelectTrigger className="mt-1.5 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currentSource?.kinds.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {humanize(opt)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex-1 max-w-sm">
                <Label className="text-[13px] font-medium">CSV File</Label>
                <Input
                  type="file"
                  ref={fileRef}
                  accept=".csv"
                  className="mt-1.5"
                />
              </div>
              <Button type="submit" size="sm" className="gap-1.5">
                <UploadIcon className="h-3.5 w-3.5" />
                Import
              </Button>
            </form>
          </CardContent>
        </Card>

      </PageContent>
    </>
  )
}
